// Source: https://gist.github.com/ilkou/7bf2dbd42a7faf70053b43034fc4b5a4?permalink_comment_id=5194437#gistcomment-5194437
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Check, ChevronDown, X } from 'lucide-react';
import React from 'react';
import SelectComponent, { components, createFilter } from 'react-select';
import AsyncSelectComponent from 'react-select/async';
import CreatableSelectComponent from 'react-select/creatable';
import { List } from 'react-window';
import { cn } from '../../lib/utils.js';
import { withoutUnselectedDisabledOptions } from './search-select-helpers';
/**
 * Gets the filter string for an option
 * - If label is a string, use that
 * - If label is not a string and filterValue exists, use filterValue
 * - If label is not a string and filterValue doesn't exist, return empty string (won't match)
 */
var getFilterString = function (option) {
    if (typeof option.label === 'string') {
        return option.label;
    }
    if (option.filterValue) {
        return option.filterValue;
    }
    // Return empty string so filtering will never match this record
    return '';
};
/**
 * styles that aligns with shadcn/ui
 */
var selectStyles = {
    controlStyles: {
        base: 'flex !min-h-8 w-full rounded-lg border border-input bg-transparent pl-2.5 py-0 pr-2 gap-1.5 text-sm transition-colors !cursor-pointer dark:bg-input/30',
        focus: 'outline-none border-ring ring-2 ring-ring/70',
        disabled: 'cursor-not-allowed opacity-50 pointer-events-none',
    },
    placeholderStyles: 'text-muted-foreground text-sm',
    valueContainerStyles: 'gap-1 py-1',
    multiValueStyles: 'inline-flex items-center gap-1.5 rounded-md border border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80 px-1.5 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
    indicatorsContainerStyles: 'gap-1',
    clearIndicatorStyles: 'p-1 rounded-md',
    indicatorSeparatorStyles: 'bg-muted',
    dropdownIndicatorStyles: 'p-1 rounded-md',
    menu: 'mt-1 p-1 border-0 ring-1 ring-foreground/10 bg-popover text-popover-foreground text-sm rounded-lg shadow-md !z-10',
    menuPortal: '',
    menuList: 'morel-scrollbar',
    groupHeadingStyles: 'py-2 px-1.5 text-muted-foreground text-xs font-semibold',
    optionStyles: {
        base: 'hover:cursor-pointer hover:bg-accent hover:text-foreground px-1.5 py-1 rounded-md !text-sm !cursor-default !select-none !outline-none font-sans',
        focus: 'active:bg-accent/90 bg-accent text-foreground',
        disabled: 'pointer-events-none opacity-50',
        selected: '',
    },
    noOptionsMessageStyles: 'text-muted-foreground py-4 text-center text-sm border border-border rounded-md',
    label: 'text-muted-foreground text-sm',
    loadingIndicatorStyles: 'flex items-center justify-center h-4 w-4 opacity-50',
    loadingMessageStyles: 'text-foreground p-2 bg-accent',
};
/**
 * This factory method is used to build custom classNames configuration
 */
export var createClassNames = function (classNames) {
    return {
        clearIndicator: function (state) { var _a; return cn(selectStyles.clearIndicatorStyles, (_a = classNames === null || classNames === void 0 ? void 0 : classNames.clearIndicator) === null || _a === void 0 ? void 0 : _a.call(classNames, state)); },
        container: function (state) { var _a; return cn((_a = classNames === null || classNames === void 0 ? void 0 : classNames.container) === null || _a === void 0 ? void 0 : _a.call(classNames, state)); },
        control: function (state) {
            var _a;
            return cn(selectStyles.controlStyles.base, state.isDisabled && selectStyles.controlStyles.disabled, state.isFocused && selectStyles.controlStyles.focus, (_a = classNames === null || classNames === void 0 ? void 0 : classNames.control) === null || _a === void 0 ? void 0 : _a.call(classNames, state));
        },
        dropdownIndicator: function (state) { var _a; return cn(selectStyles.dropdownIndicatorStyles, (_a = classNames === null || classNames === void 0 ? void 0 : classNames.dropdownIndicator) === null || _a === void 0 ? void 0 : _a.call(classNames, state)); },
        group: function (state) { var _a; return cn((_a = classNames === null || classNames === void 0 ? void 0 : classNames.group) === null || _a === void 0 ? void 0 : _a.call(classNames, state)); },
        groupHeading: function (state) { var _a; return cn(selectStyles.groupHeadingStyles, (_a = classNames === null || classNames === void 0 ? void 0 : classNames.groupHeading) === null || _a === void 0 ? void 0 : _a.call(classNames, state)); },
        indicatorsContainer: function (state) { var _a; return cn(selectStyles.indicatorsContainerStyles, (_a = classNames === null || classNames === void 0 ? void 0 : classNames.indicatorsContainer) === null || _a === void 0 ? void 0 : _a.call(classNames, state)); },
        indicatorSeparator: function (state) { var _a; return cn(selectStyles.indicatorSeparatorStyles, (_a = classNames === null || classNames === void 0 ? void 0 : classNames.indicatorSeparator) === null || _a === void 0 ? void 0 : _a.call(classNames, state)); },
        input: function (state) { var _a; return cn((_a = classNames === null || classNames === void 0 ? void 0 : classNames.input) === null || _a === void 0 ? void 0 : _a.call(classNames, state)); },
        loadingIndicator: function (state) { var _a; return cn(selectStyles.loadingIndicatorStyles, (_a = classNames === null || classNames === void 0 ? void 0 : classNames.loadingIndicator) === null || _a === void 0 ? void 0 : _a.call(classNames, state)); },
        loadingMessage: function (state) { var _a; return cn(selectStyles.loadingMessageStyles, (_a = classNames === null || classNames === void 0 ? void 0 : classNames.loadingMessage) === null || _a === void 0 ? void 0 : _a.call(classNames, state)); },
        menu: function (state) { var _a; return cn(selectStyles.menu, (_a = classNames === null || classNames === void 0 ? void 0 : classNames.menu) === null || _a === void 0 ? void 0 : _a.call(classNames, state)); },
        menuList: function (state) { var _a; return cn(selectStyles.menuList, (_a = classNames === null || classNames === void 0 ? void 0 : classNames.menuList) === null || _a === void 0 ? void 0 : _a.call(classNames, state)); },
        menuPortal: function (state) { var _a; return cn(selectStyles.menuPortal, (_a = classNames === null || classNames === void 0 ? void 0 : classNames.menuPortal) === null || _a === void 0 ? void 0 : _a.call(classNames, state)); },
        multiValue: function (state) { var _a; return cn(selectStyles.multiValueStyles, (_a = classNames === null || classNames === void 0 ? void 0 : classNames.multiValue) === null || _a === void 0 ? void 0 : _a.call(classNames, state)); },
        multiValueLabel: function (state) { var _a; return cn((_a = classNames === null || classNames === void 0 ? void 0 : classNames.multiValueLabel) === null || _a === void 0 ? void 0 : _a.call(classNames, state)); },
        multiValueRemove: function (state) { var _a; return cn((_a = classNames === null || classNames === void 0 ? void 0 : classNames.multiValueRemove) === null || _a === void 0 ? void 0 : _a.call(classNames, state)); },
        noOptionsMessage: function (state) { var _a; return cn(selectStyles.noOptionsMessageStyles, (_a = classNames === null || classNames === void 0 ? void 0 : classNames.noOptionsMessage) === null || _a === void 0 ? void 0 : _a.call(classNames, state)); },
        option: function (state) {
            var _a;
            return cn(selectStyles.optionStyles.base, state.isFocused && selectStyles.optionStyles.focus, state.isDisabled && selectStyles.optionStyles.disabled, state.isSelected && selectStyles.optionStyles.selected, (_a = classNames === null || classNames === void 0 ? void 0 : classNames.option) === null || _a === void 0 ? void 0 : _a.call(classNames, state));
        },
        placeholder: function (state) { var _a; return cn(selectStyles.placeholderStyles, (_a = classNames === null || classNames === void 0 ? void 0 : classNames.placeholder) === null || _a === void 0 ? void 0 : _a.call(classNames, state)); },
        singleValue: function (state) { var _a; return cn((_a = classNames === null || classNames === void 0 ? void 0 : classNames.singleValue) === null || _a === void 0 ? void 0 : _a.call(classNames, state)); },
        valueContainer: function (state) { var _a; return cn(selectStyles.valueContainerStyles, (_a = classNames === null || classNames === void 0 ? void 0 : classNames.valueContainer) === null || _a === void 0 ? void 0 : _a.call(classNames, state)); },
    };
};
export var defaultClassNames = createClassNames({});
export var defaultStyles = {
    input: function (base) { return (__assign(__assign({}, base), { 'input:focus': {
            boxShadow: 'none',
        } })); },
    multiValueLabel: function (base) { return (__assign(__assign({}, base), { whiteSpace: 'normal', overflow: 'visible' })); },
    control: function (base) { return (__assign(__assign({}, base), { transition: 'none' })); },
    menuList: function (base) { return (__assign(__assign({}, base), { '::-webkit-scrollbar': {
            background: 'transparent',
        }, '::-webkit-scrollbar-track': {
            background: 'transparent',
        }, '::-webkit-scrollbar-thumb': {
            background: 'var(--border)',
        }, '::-webkit-scrollbar-thumb:hover': {
            background: 'transparent',
        } })); },
    menuPortal: function (base) { return (__assign(__assign({}, base), { zIndex: 100 })); },
};
/**
 * React select custom components
 */
export var DropdownIndicator = function (props) {
    return (<components.DropdownIndicator {...props}>
      <ChevronDown className="h-4 w-4 opacity-50"/>
    </components.DropdownIndicator>);
};
export var ClearIndicator = function (props) {
    return (<components.ClearIndicator {...props}>
      <X className="h-4 w-4 opacity-50"/>
    </components.ClearIndicator>);
};
export var MultiValueRemove = function (props) {
    return (<components.MultiValueRemove {...props}>
      <X className="h-3.5 w-3.5 opacity-50"/>
    </components.MultiValueRemove>);
};
export var Option = function (props) {
    return (<components.Option {...props}>
      <div className="flex items-center justify-between">
        <div>{props.label}</div>
        {props.isSelected && <Check className="h-4 w-4 opacity-50"/>}
      </div>
    </components.Option>);
};
// react-select sets its own aria-describedby on the input, dropping the one FormControl passes in
var SelectInput = function (props) {
    var formDescribedBy = props.selectProps["aria-describedby"];
    var describedBy = [props['aria-describedby'], formDescribedBy].filter(Boolean).join(' ');
    return <components.Input {...props} aria-describedby={describedBy || undefined}/>;
};
// Using Menu and MenuList fixes the scrolling behavior
export var Menu = function (props) {
    return <components.Menu {...props}>{props.children}</components.Menu>;
};
export var MenuList = function (props) {
    var children = props.children, maxHeight = props.maxHeight, options = props.options, _a = props.virtualRowHeight, virtualRowHeight = _a === void 0 ? 35 : _a;
    var childrenArray = React.Children.toArray(children);
    // Check if we have grouped options by looking at the options prop structure
    var hasGroupedOptions = Array.isArray(options) &&
        options.some(function (option) {
            return option && typeof option === 'object' && 'options' in option && Array.isArray(option.options);
        });
    var requiresVirtualization = childrenArray && childrenArray.length > 100;
    // If we have grouped options or a small number of options, use the default MenuList
    // as FixedSizeList doesn't handle variable height items like group headings well
    if (hasGroupedOptions || !requiresVirtualization) {
        return (<components.MenuList {...props} className="[&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-thumb:hover]:!bg-border [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent"/>);
    }
    var calculateHeight = function () {
        // When using children it resizes correctly
        var totalHeight = childrenArray.length * virtualRowHeight;
        return totalHeight < maxHeight ? totalHeight : maxHeight;
    };
    var height = calculateHeight();
    return (<List style={{ height: height }} rowCount={childrenArray.length} rowHeight={virtualRowHeight} rowProps={{}} rowComponent={function (_a) {
        var index = _a.index, style = _a.style;
        return <div style={style}>{childrenArray[index]}</div>;
    }}/>);
};
var BaseSelect = function (props, ref) {
    var _a = props.styles, styles = _a === void 0 ? defaultStyles : _a, _b = props.classNames, classNames = _b === void 0 ? defaultClassNames : _b, _c = props.components, components = _c === void 0 ? {} : _c, hideUnselectedDisabledOptions = props.hideUnselectedDisabledOptions, options = props.options, rest = __rest(props, ["styles", "classNames", "components", "hideUnselectedDisabledOptions", "options"]);
    var instanceId = React.useId();
    return (<SelectComponent ref={ref} instanceId={instanceId} options={hideUnselectedDisabledOptions
            ? withoutUnselectedDisabledOptions(options, rest.value)
            : options} unstyled filterOption={createFilter({
            matchFrom: 'any',
            stringify: function (option) { return getFilterString(option); },
        })} components={__assign({ DropdownIndicator: DropdownIndicator, ClearIndicator: ClearIndicator, Input: SelectInput, MultiValueRemove: MultiValueRemove, Option: Option, Menu: Menu, MenuList: MenuList }, components)} styles={styles} classNames={classNames} {...rest}/>);
};
export var SearchSelect = React.forwardRef(BaseSelect);
/**
 * Async version of the select component
 */
var BaseAsyncSelect = function (props, ref) {
    var _a = props.styles, styles = _a === void 0 ? defaultStyles : _a, _b = props.classNames, classNames = _b === void 0 ? defaultClassNames : _b, _c = props.components, components = _c === void 0 ? {} : _c, rest = __rest(props, ["styles", "classNames", "components"]);
    var instanceId = React.useId();
    return (<AsyncSelectComponent ref={ref} instanceId={instanceId} unstyled filterOption={createFilter({
            matchFrom: 'any',
            stringify: function (option) { return getFilterString(option); },
        })} components={__assign({ DropdownIndicator: DropdownIndicator, ClearIndicator: ClearIndicator, Input: SelectInput, MultiValueRemove: MultiValueRemove, Option: Option, Menu: Menu, MenuList: MenuList }, components)} styles={styles} classNames={classNames} {...rest}/>);
};
export var AsyncSelect = React.forwardRef(BaseAsyncSelect);
/**
 * Creatable version of the select component
 */
var BaseCreatableSelect = function (props, ref) {
    var _a = props.styles, styles = _a === void 0 ? defaultStyles : _a, _b = props.classNames, classNames = _b === void 0 ? defaultClassNames : _b, _c = props.components, components = _c === void 0 ? {} : _c, rest = __rest(props, ["styles", "classNames", "components"]);
    var instanceId = React.useId();
    return (<CreatableSelectComponent ref={ref} instanceId={instanceId} unstyled filterOption={createFilter({
            matchFrom: 'any',
            stringify: function (option) { return getFilterString(option); },
        })} components={__assign({ DropdownIndicator: DropdownIndicator, ClearIndicator: ClearIndicator, Input: SelectInput, MultiValueRemove: MultiValueRemove, Option: Option, Menu: Menu, MenuList: MenuList }, components)} styles={styles} classNames={classNames} {...rest}/>);
};
export var CreatableSelect = React.forwardRef(BaseCreatableSelect);
